/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo } from 'react';
import TenantFeedContext from './TenantFeedContext';
import initialTenantFeedState from './initialTenantFeedState';
import tenantFeedReducer from './reducer/tenantFeed.reducer';
import * as action from './reducer/tenantFeed.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { ChildrenProps } from 'interfaces/general.interface';
import { abortCatalogController } from 'utils/abortController';

function TenantFeedProvider(props: ChildrenProps) {
  const [tenantFeedState, dispatch] = useReducer(
    tenantFeedReducer,
    initialTenantFeedState
  );
  const { getAccessTokenSilently } = useAuth0();
  const { t: translate } = useTranslation();

  const getTenantFeed = useCallback(async (tenantId: string, countryId: string) => {
    const token = await getAccessTokenSilently();
    return await action.getTenantFeedAction({
      dispatch,
      tenantId,
      countryId,
      token,
      translate,
    });
  }, []);

  const getProfileVideosByBrand = useCallback(async (brandId: string) => {
    const signal = await abortCatalogController();
    const token = await getAccessTokenSilently();
    return await action.getProfileVideosByBrandAction(
      dispatch,
      brandId,
      signal,
      translate,
      token
    );
  }, []);

  const getProfileProductsByBrand = useCallback(
    async (brandId: string) => {
      const token = await getAccessTokenSilently();
      return await action.getProfileProductsByBrandAction({
        dispatch,
        brandId,
        translate,
        token,
      });
    },
    [getAccessTokenSilently]
  );

  const memoProvider = useMemo(
    () => ({
      ...tenantFeedState,
      getTenantFeed,
      getProfileVideosByBrand,
      getProfileProductsByBrand,
    }),
    [tenantFeedState, getTenantFeed, getProfileVideosByBrand, getProfileProductsByBrand]
  );

  return (
    <TenantFeedContext.Provider value={memoProvider}>
      {props.children}
    </TenantFeedContext.Provider>
  );
}

export default memo(TenantFeedProvider);
